<script lang="ts">
  import { onMount } from "svelte";
  import editIcon from "./assets/edit.svg?raw";
  import learnIcon from "./assets/learn.svg?raw";
  import duplicateIcon from "./assets/duplicate.svg?raw";
  import linkIcon from "./assets/link.svg?raw";
  import copyrightIcon from "./assets/copyright.svg?raw";
  import { stopPropagation, preventDefault } from "./genericHandlers";
  import { showToastWrapper } from "./global";
  import type { Scene } from "./Soundboard";
  import { isFocusedScene } from "./global";
  import { outclick } from "./lib/interactions";
  import Modal from "./Modal.svelte";
  import {
    calculateCopyrightNotice,
    type CopyrightNotice,
  } from "./lib/copyright";
  import GuidedScene from "./GuidedScene.svelte";
  import { GuidedScene as GuidedSceneType } from "./GuidedScene";
  import { userSettings } from "./store";
  import { UserSettingIds } from "./lib/UserSettings";

  export let nameChangeCallback: (newName: string) => void;
  export let duplicateCallback: (evt) => void;
  export let scene: Scene;

  let editing = false;
  let nameInput;
  let instructions: string | null = null;
  let instructionsLink: string | null = null;
  let permalinkId: string | null = null;

  let titleContainer: HTMLElement;

  onMount(() => {
    if (isFocusedScene(scene)) {
      titleContainer.scrollIntoView(true);
    }
  });

  $: if (scene) {
    if (scene.metadata) {
      instructions = scene.metadata.instructions;
      instructionsLink = scene.metadata.instructionsLink;
    }
  }

  const handleNameChangeRequest = () => {
    nameChangeCallback(nameInput.value);
    editing = false;
  };
  const startEditing = () => {
    editing = true;
  };

  const COPY_LINK = "Copy Scene Link";
  const LINK_COPIED = "Link Copied!";
  let copyLinkText = COPY_LINK;
  function handleLinkClick(evt) {
    const permalink = scene.Permalink();
    console.log("Now we should be copying permalink: " + permalink);
    navigator.clipboard.writeText(permalink);
    showToastWrapper("Scene link copied to clipboard!", false);
    copyLinkText = LINK_COPIED;
    setTimeout(() => {
      copyLinkText = COPY_LINK;
    }, 3000);
  }

  function handleDuplicateClick(evt) {
    duplicateCallback(evt);
  }

  let copyButton: HTMLButtonElement;
  let showCopyrightInfo = false;
  let copyrightNotice: CopyrightNotice | null = null;
  let copyButtonName: string = "Copy";
  async function handleCopyrightClick(evt) {
    if (scene instanceof GuidedSceneType) {
      copyrightNotice = await calculateCopyrightNotice(
        scene as GuidedSceneType,
      );
      showCopyrightInfo = true;
    }
  }

  function copyNoticeToClipboard(evt) {
    navigator.clipboard.writeText(copyrightNotice.usableSoundNotice);
    copyButtonName = "Copied!";
    setTimeout(() => {
      copyButtonName = "Copy";
    }, 3000);
  }
</script>

<div id={scene.permalinkId} bind:this={titleContainer}>
  {#if editing}
    <form on:submit={handleNameChangeRequest}>
      <input
        bind:this={nameInput}
        type="text"
        id="lane-name-{scene.id}"
        name="lane-name"
        value={scene.name}
        class="soundboard-lane-title-input contrast font-size-1-5-rem"
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
        use:outclick={handleNameChangeRequest}
        on:keyup|stopPropagation|preventDefault
      />
    </form>
  {:else}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="soundboard-lane-title-container editable-icon"
      tabindex="0"
      on:click={(evt) => {
        stopPropagation(evt);
        preventDefault(evt);
      }}
    >
      <div class="scene-title">
        {#if instructionsLink}
          <!-- TODO BL: This onclick is a hack for the fact that clicking on this thing doesn't do anything -->
          <a
            href={instructionsLink}
            target="_blank"
            class="no-tooltip-underline tooltip-no-shadow"
            data-tooltip="Learn how to use this scene!"
            data-placement="right"
            on:click={() => window.open(instructionsLink, "_blank")}
          >
            {@html learnIcon}
          </a>
        {/if}
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div tabindex="0" on:dblclick={startEditing}>
          <strong>
            {scene["name"]}
          </strong>
        </div>
      </div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="hover-target no-tooltip-underline tooltip-no-shadow cursor-pointer"
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
        on:click={startEditing}
        tabindex="0"
        data-tooltip="Change Name"
      >
        {@html editIcon}
      </div>
      <div
        class="hover-target no-tooltip-underline tooltip-no-shadow cursor-pointer"
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
        on:click={handleLinkClick}
        data-tooltip={copyLinkText}
      >
        {@html linkIcon}
      </div>
      <div
        class="hover-target no-tooltip-underline tooltip-no-shadow cursor-pointer"
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
        on:click={handleDuplicateClick}
        data-tooltip="Duplicate Scene"
      >
        {@html duplicateIcon}
      </div>
      {#if $userSettings.getValue(UserSettingIds.LicenseToolsEnabled)}
        <div
          class="hover-target no-tooltip-underline tooltip-no-shadow cursor-pointer"
          on:pointerdown={stopPropagation}
          on:mousedown={stopPropagation}
          on:click={handleCopyrightClick}
          data-tooltip="Copyright Info"
        >
          {@html copyrightIcon}
        </div>
      {/if}
    </div>
  {/if}
</div>
<Modal bind:showModal={showCopyrightInfo}>
  <p
    slot="header"
    class:red={copyrightNotice && copyrightNotice.unusableSoundsNotice}
  >
    Copyright Notice
  </p>
  {#if copyrightNotice}
    <div on:pointerdown={stopPropagation}>
      {#if copyrightNotice.unusableSoundsNotice}
        <p class="red">These sounds cannot be used in public:</p>
        <code class="red">{copyrightNotice.unusableSoundsNotice}</code>
      {:else}
        <p>Paste the following into your description</p>
        <small>
          <code>
            {copyrightNotice.usableSoundNotice}
          </code>
        </small>
      {/if}
    </div>
  {/if}
  <div slot="footer">
    <div class="container grid">
      {#if copyrightNotice && copyrightNotice.unusableSoundsNotice === ""}
        <button
          class="contrast outline full-w"
          bind:this={copyButton}
          on:click={copyNoticeToClipboard}>{copyButtonName}</button
        >
      {/if}
      <button
        class="contrast outline full-w"
        on:click={() => {
          showCopyrightInfo = false;
        }}>Close</button
      >
    </div>
  </div>
</Modal>
